/**
 * Add an application — `POST /recruiting/applications`, WP-W3.
 *
 * An application ties a candidate to a requisition, so both ids are required and the
 * submit stays disabled until both are chosen — a true sentence about the operator
 * ("you have not picked both"), not about permission, and the shape that keeps a `400`
 * from being how a mandatory field is learned. A duplicate `(candidate, requisition)`
 * answers `409 WORKFORCE_CONFLICT`, shown as the server's own sentence.
 *
 * ── Pickers, or a plain id when the list is out of reach ─────────────────────
 *
 * The candidate list shares this screen's own permission (`recruiting.view_candidates`),
 * so it is always available and the candidate is a `<select>`. Requisition titles come
 * from `GET /recruiting/requisitions`, which reads on `recruiting.view_jobs`; a session
 * that holds `view_candidates` but not `view_jobs` reaches the pipeline and cannot read
 * that list — so when the options are `null` the requisition becomes a plain id input,
 * the same honest fallback the Jobs create form uses for ids it has no directory for.
 * The optional recruiter is an `EmployeePicker` (Task T2-B), reading `GET /employees`
 * under the server's own resolved scope — it was the last typed-id box on this form,
 * standing under a line that promised a picker "sẽ có sau" while the picker was three
 * files away (finish review, M2).
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type {
  RecruitingApplication,
  RecruitingApplicationCreateRequest,
} from '../../api/recruiting';
import type { WorkforceApiError } from '../../api/errors';
import { EmployeePicker, type EmployeePickerValue } from '../../components/EmployeePicker';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';

export interface PickerOption {
  readonly id: string;
  readonly label: string;
}

export interface CreateApplicationFormProps {
  readonly candidateOptions: readonly PickerOption[];
  /** `null` when the requisitions list is out of reach — the field falls back to an id input. */
  readonly requisitionOptions: readonly PickerOption[] | null;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly created: RecruitingApplication | null;
  readonly onSubmit: (body: RecruitingApplicationCreateRequest) => void;
}

export function CreateApplicationForm({
  candidateOptions,
  requisitionOptions,
  pending,
  error,
  created,
  onSubmit,
}: CreateApplicationFormProps): ReactNode {
  const { t } = useI18n();
  const [candidateId, setCandidateId] = useState('');
  const [requisitionId, setRequisitionId] = useState('');
  const [recruiter, setRecruiter] = useState<EmployeePickerValue | null>(null);

  const ready = candidateId.trim().length > 0 && requisitionId.trim().length > 0;

  const build = (): RecruitingApplicationCreateRequest => ({
    candidate_id: candidateId.trim(),
    requisition_id: requisitionId.trim(),
    ...(recruiter === null ? {} : { assigned_recruiter_employee_id: recruiter.id }),
  });

  return (
    <section className="wp-section" aria-labelledby="w3-create-heading" data-testid="w3-create">
      <h2 className="wp-section__title" id="w3-create-heading">
        {t('recruiting.pipeline.create.title')}
      </h2>
      <p className="wp-small wp-muted">{t('recruiting.pipeline.create.intro')}</p>

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready || pending) return;
          onSubmit(build());
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w3-create-candidate">
            {t('recruiting.pipeline.create.candidate')}
          </label>
          <select
            className="wp-field__input"
            id="w3-create-candidate"
            data-testid="w3-create-candidate"
            value={candidateId}
            onChange={(event) => {
              setCandidateId(event.target.value);
            }}
          >
            <option value="">{t('recruiting.pipeline.create.candidatePlaceholder')}</option>
            {candidateOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w3-create-requisition">
            {t('recruiting.pipeline.create.requisition')}
          </label>
          {requisitionOptions === null ? (
            <>
              <input
                className="wp-field__input"
                id="w3-create-requisition"
                data-testid="w3-create-requisition"
                type="text"
                value={requisitionId}
                autoComplete="off"
                onChange={(event) => {
                  setRequisitionId(event.target.value);
                }}
              />
              <p className="wp-small wp-muted">
                {t('recruiting.pipeline.create.requisitionIdHint')}
              </p>
            </>
          ) : (
            <select
              className="wp-field__input"
              id="w3-create-requisition"
              data-testid="w3-create-requisition"
              value={requisitionId}
              onChange={(event) => {
                setRequisitionId(event.target.value);
              }}
            >
              <option value="">{t('recruiting.pipeline.create.requisitionPlaceholder')}</option>
              {requisitionOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="wp-filters__group">
          <EmployeePicker
            label={t('recruiting.pipeline.create.recruiter')}
            testId="w3-create-recruiter"
            value={recruiter}
            onChange={setRecruiter}
          />
          <p className="wp-small wp-muted">{t('recruiting.pipeline.create.recruiterHint')}</p>
        </div>

        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w3-create-submit"
            disabled={!ready || pending}
          >
            {pending
              ? t('recruiting.pipeline.create.submitting')
              : t('recruiting.pipeline.create.submit')}
          </button>
        </div>
      </form>

      {error === null ? null : (
        <ApiErrorNote
          error={error}
          subject={t('recruiting.pipeline.subjectWord')}
          testId="w3-create-error"
        />
      )}
      {created === null ? null : (
        <StatusNote
          severity="low"
          title={t('recruiting.pipeline.create.ok')}
          testId="w3-create-ok"
        />
      )}
    </section>
  );
}
